import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import api from '../services/api';
import LiveMatchBadge from './LiveMatchBadge';
import { isKnockoutGameStage, isWcLeagueCode } from '../utils/leagues';
import { isGameLive } from '../utils/homeGameDisplayOrder';
import {
  formatStandingsGroupLabel,
  pickStandingsTableForMatch,
  rowHighlightsMatch
} from '../utils/leagueTableStandings';
import {
  noteLiveTouchedGames,
  projectLiveStandings,
  selectStandingsOverlays
} from '../utils/projectLiveStandings';
import { formatTeamDisplayName } from '../utils/formatTeamDisplayName';
import './MatchLeagueTablePopover.css';

function formatLiveScore(game) {
  const home = game?.homeScore;
  const away = game?.awayScore;
  if (home == null || away == null) return '–';
  return `${home}–${away}`;
}

function selectLiveGames(games) {
  return (games || [])
    .filter((g) => g?.gameId && isGameLive(g))
    .slice()
    .sort((a, b) => {
      const da = Number(a.gameDate);
      const db = Number(b.gameDate);
      if (Number.isFinite(da) && Number.isFinite(db) && da !== db) return da - db;
      return String(a.gameId).localeCompare(String(b.gameId));
    });
}

function rowInLiveGames(row, liveGames) {
  return liveGames.some((g) => rowHighlightsMatch(row, g.homeTeam, g.awayTeam));
}

function LiveFixturesStrip({ liveGames }) {
  if (liveGames.length === 0) return null;

  return (
    <div
      className="league-table-popover__live"
      data-testid="league-table-live-fixtures"
      aria-label="Live matches"
    >
      {liveGames.map((game) => (
        <div key={game.gameId} className="league-table-popover__live-row">
          <span className="game-status__live-dot" aria-hidden="true" />
          <span className="league-table-popover__live-fixture">
            <span className="league-table-popover__live-team">
              {formatTeamDisplayName(game.homeTeam)}
            </span>
            <span className="league-table-popover__live-score">{formatLiveScore(game)}</span>
            <span className="league-table-popover__live-team">
              {formatTeamDisplayName(game.awayTeam)}
            </span>
          </span>
          <LiveMatchBadge game={game} />
        </div>
      ))}
    </div>
  );
}

/** @type {Map<string, unknown>} session cache — no TTL; cleared on page reload */
const standingsResponseCache = new Map();

function getCachedStandings(leagueCode) {
  return standingsResponseCache.has(leagueCode)
    ? standingsResponseCache.get(leagueCode)
    : null;
}

function setCachedStandings(leagueCode, payload) {
  standingsResponseCache.set(leagueCode, payload);
}

function TableGlyphIcon() {
  return (
    <svg className="match-league-table-icon-svg" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        d="M4 4h16v16H4V4zm0 5.5h16M4 14.5h16M10 4v16M14 4v16"
      />
    </svg>
  );
}

/**
 * Opens official league table from the table icon (click only). Backdrop or any tap on the panel closes it.
 * Fetches once per league per page session; live/finished overlays come from `games` (AppSync-merged).
 */
const MatchLeagueTablePopover = ({ leagueCode, homeTeam, awayTeam, stage, games = [] }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [baseSnapshot, setBaseSnapshot] = useState(null);
  const [dockStyle, setDockStyle] = useState(null);
  const anchorRef = useRef(null);

  const load = useCallback(async () => {
    if (!leagueCode) return;
    const cached = getCachedStandings(leagueCode);
    if (cached != null) {
      setBaseSnapshot(cached);
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/football/${leagueCode}/table`);
      setCachedStandings(leagueCode, res.data);
      setBaseSnapshot(res.data);
    } catch (err) {
      setBaseSnapshot(null);
      setError(err.response?.data?.error || 'Could not load table');
    } finally {
      setLoading(false);
    }
  }, [leagueCode]);

  useEffect(() => {
    setBaseSnapshot(null);
    setError('');
  }, [leagueCode]);

  useEffect(() => {
    if (!open) return undefined;
    load();
  }, [open, load]);

  // Track live games even while the popover is closed so FINISHED still overlays.
  useEffect(() => {
    noteLiveTouchedGames(games);
  }, [games]);

  const displaySnapshot = useMemo(() => {
    if (!baseSnapshot) return null;
    const overlays = selectStandingsOverlays(games);
    return projectLiveStandings(baseSnapshot, overlays);
  }, [baseSnapshot, games]);

  const liveGames = useMemo(() => selectLiveGames(games), [games]);

  const isCompactTable = isWcLeagueCode(leagueCode);

  const updateDockPosition = useCallback(() => {
    if (!open || !anchorRef.current) return;
    const anchorEl = anchorRef.current;
    const main = anchorEl.closest('.main-content');
    const col = main?.getBoundingClientRect();
    const colWidth = col ? col.width : Math.min(window.innerWidth - 24, 640);
    const width = isCompactTable
      ? Math.min(22 * 16, colWidth)
      : colWidth;
    const centerLeft = col ? col.left + col.width / 2 : window.innerWidth / 2;
    const top = window.innerHeight / 2;
    const maxHeight = isCompactTable
      ? undefined
      : Math.min(window.innerHeight * 0.85, window.innerHeight - 32);
    setDockStyle({
      left: centerLeft,
      width,
      top,
      maxHeight,
      transform: 'translate(-50%, -50%)'
    });
  }, [open, isCompactTable]);

  useLayoutEffect(() => {
    if (!open) {
      setDockStyle(null);
      return undefined;
    }
    updateDockPosition();
    window.addEventListener('scroll', updateDockPosition, true);
    window.addEventListener('resize', updateDockPosition);
    return () => {
      window.removeEventListener('scroll', updateDockPosition, true);
      window.removeEventListener('resize', updateDockPosition);
    };
  }, [open, updateDockPosition, loading, displaySnapshot, error]);

  if (!leagueCode) return null;
  if (isWcLeagueCode(leagueCode) && isKnockoutGameStage(stage)) return null;

  const picked = displaySnapshot
    ? pickStandingsTableForMatch(
      displaySnapshot.tables ? displaySnapshot : displaySnapshot.standings,
      homeTeam,
      awayTeam
    )
    : { rows: [], group: null };
  const rows = picked.rows;
  const baseTitle = displaySnapshot?.competition?.name || 'League table';
  const groupLabel = formatStandingsGroupLabel(picked.group);
  const title = groupLabel ? `${baseTitle} · ${groupLabel}` : baseTitle;
  const showTable = !loading && !error && rows.length > 0;
  const bodyIdle = loading || !!error || (!loading && !error && rows.length === 0);

  const closePanel = () => {
    setOpen(false);
  };

  const portalContent = open && dockStyle && (
    <>
      <button
        type="button"
        className="league-table-popover__backdrop"
        aria-label="Close standings"
        onClick={closePanel}
      />
      <div
        className={`league-table-popover league-table-popover--docked${isCompactTable ? ' league-table-popover--compact' : ''}`}
        style={{
          position: 'fixed',
          left: dockStyle.left,
          width: dockStyle.width,
          top: dockStyle.top,
          maxHeight: dockStyle.maxHeight,
          transform: dockStyle.transform,
          zIndex: 200
        }}
        role="dialog"
        aria-label={title}
        onClick={closePanel}
      >
        <div className="league-table-popover__head">
          <div className="league-table-popover__head-main">
            <span className="league-table-popover__title">{title}</span>
            {displaySnapshot?.updatedAt != null && (
              <span className="league-table-popover__updated">
                Updated {new Date(displaySnapshot.updatedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            )}
          </div>
          <button
            type="button"
            className="league-table-popover__close"
            aria-label="Close standings"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              closePanel();
            }}
          >
            ×
          </button>
        </div>
        <div className={`league-table-popover__body ${bodyIdle ? 'league-table-popover__body--idle' : ''}`}>
          {loading && <div className="league-table-popover__muted">Loading…</div>}
          {!loading && error && <div className="league-table-popover__err">{error}</div>}
          {!loading && !error && <LiveFixturesStrip liveGames={liveGames} />}
          {showTable && (
            <div className="league-table-popover__scroll">
              <table className="league-table-popover__table">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Team</th>
                    <th scope="col">P</th>
                    <th scope="col">GD</th>
                    <th scope="col">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const live = rowInLiveGames(row, liveGames);
                    const matchTeam = rowHighlightsMatch(row, homeTeam, awayTeam);
                    const highlight = !live && matchTeam;
                    const name = row.team?.name || row.team?.shortName || row.name || row.shortName || '—';
                    const played = row.playedGames ?? row.played;
                    const rowClass = [
                      live && 'league-table-popover__tr--live',
                      live && matchTeam && 'league-table-popover__tr--live-focus',
                      highlight && 'league-table-popover__tr--hl'
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <tr key={`${row.position}-${name}`} className={rowClass}>
                        <td>{row.position}</td>
                        <td className="league-table-popover__team">{name}</td>
                        <td>{played}</td>
                        <td>
                          {row.goalDifference > 0 ? `+${row.goalDifference}` : row.goalDifference}
                        </td>
                        <td className="league-table-popover__pts">{row.points}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="league-table-popover__muted">No table rows in snapshot.</div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      <div ref={anchorRef} className="league-table-popover-anchor">
        <button
          type="button"
          className="league-table-popover-trigger"
          aria-label="Show league table"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen((o) => !o);
          }}
        >
          <TableGlyphIcon />
        </button>
      </div>
      {portalContent && typeof document !== 'undefined' && document.body
        ? ReactDOM.createPortal(portalContent, document.body)
        : null}
    </>
  );
};

export default MatchLeagueTablePopover;
